import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ExternalLink } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { selfAPI } from '../services/api';

export default function EmployeeHospitals() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const debouncedQ = useDebouncedValue(q);
  const debouncedCity = useDebouncedValue(city);

  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    selfAPI.searchHospitals({
      q: debouncedQ || undefined,
      city: debouncedCity || undefined,
    })
      .then(({ data: res }) => {
        if (!cancelled) setData(res);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setSearching(false);
        }
      });
    return () => { cancelled = true; };
  }, [debouncedQ, debouncedCity]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to="/" className="text-primary-600 text-sm">← Back to My Coverage</Link>
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Building2 className="w-7 h-7 text-primary-600" />
          Cashless Hospital Search
        </h1>
        <p className="text-slate-500 mt-1">
          Network hospitals for policy {data?.policyNumber || '—'} ({data?.providerName || 'Insurer'})
        </p>
      </div>

      {data?.isExternalUrl && data.externalUrl && (
        <div className="card bg-primary-50 border-primary-100 text-sm">
          <p className="mb-2">Your insurer maintains an online hospital directory.</p>
          <a href={data.externalUrl} target="_blank" rel="noreferrer" className="text-primary-700 font-medium inline-flex items-center gap-1">
            Open hospital list <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      )}

      <div className="card grid sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <SearchInput
            label="Search hospital"
            placeholder="Hospital name…"
            value={q}
            onChange={setQ}
          />
        </div>
        <SearchInput
          label="City"
          placeholder="e.g. Mumbai"
          value={city}
          onChange={setCity}
        />
        {searching && (
          <p className="sm:col-span-3 text-sm text-slate-500">Updating results…</p>
        )}
      </div>

      <div className="card">
        <h2 className="font-semibold mb-3">
          {data?.hospitals?.length ?? 0} hospital(s) found
          {data?.total > 0 && data.hospitals.length < data.total
            ? ` (of ${data.total} in network)`
            : ''}
        </h2>
        {data?.hospitals?.length === 0 ? (
          <p className="text-sm text-slate-500">No hospitals match your search. Try a different city or name.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.hospitals.map((h) => (
              <li key={h.id} className="py-3 text-sm">
                <p className="font-medium">{h.name}</p>
                {(h.city || h.state) && (
                  <p className="text-slate-500">{[h.city, h.state].filter(Boolean).join(', ')}</p>
                )}
                {h.address_line1 && <p className="text-slate-500">{h.address_line1}</p>}
                {h.contact_phone && <p className="text-slate-500">{h.contact_phone}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
